import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import { companyAddressLines, companyRegistrationLine } from '../../lib/companySettings'
import type { InvoicePDFData } from './fetchInvoicePDFData'
import { invoiceCategoryLabel, isDutyVat } from '../../lib/invoiceCategory'

// Layout verified against docs/invoice-samples/K8767.pdf (Import Invoice) and K8766D.pdf
// (Duty & VAT Invoice) -- both real Sage-exported invoices for the same job (Gary, 2026-09-28).
// Structural conventions (StyleSheet grouping, address-box pattern, table pattern) copied from
// NotePDF.tsx rather than reinvented.

const S = StyleSheet.create({
  page:       { padding: 36, fontFamily: 'Helvetica', fontSize: 9, color: '#222' },
  header:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  logoBox:    { flex: 1 },
  logoText:   { fontSize: 20, fontFamily: 'Helvetica-Bold', color: '#1e3a5f', letterSpacing: 2 },
  addrText:   { fontSize: 7.5, color: '#555', marginTop: 2 },
  title:      { fontSize: 20, fontFamily: 'Helvetica-Bold', textAlign: 'center', marginVertical: 10, color: '#111' },

  refRow2:    { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 },
  billBox:    { maxWidth: 220 },
  billLine:   { fontSize: 9, marginBottom: 1.5 },
  refBox:     { alignItems: 'flex-end', minWidth: 190 },
  refRow:     { flexDirection: 'row', marginBottom: 2 },
  refLbl:     { fontSize: 8, color: '#555' },
  refVal:     { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#111' },

  addrRow:    { flexDirection: 'row', gap: 8, marginBottom: 8 },
  addrBox:    { flex: 1, border: 1, borderColor: '#d1d5db', borderRadius: 3, padding: '5 7' },
  addrBoxHdr: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#555', textTransform: 'uppercase', marginBottom: 3, borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb', paddingBottom: 2 },
  addrLine:   { fontSize: 8, marginBottom: 1.5 },

  shipRow:    { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8, borderWidth: 0.5, borderColor: '#d1d5db', borderRadius: 3, padding: '5 7' },
  shipItem:   { width: '33%', marginBottom: 3 },
  shipLbl:    { fontSize: 7, color: '#666' },
  shipVal:    { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#111' },
  remarksVal: { fontSize: 7.5, color: '#444', marginTop: 2 },

  table:      { marginBottom: 6 },
  thead:      { flexDirection: 'row', backgroundColor: '#1e3a5f', padding: '3 6' },
  trow:       { flexDirection: 'row', padding: '3 6', borderBottomWidth: 0.5, borderBottomColor: '#f3f4f6' },
  trowAlt:    { flexDirection: 'row', padding: '3 6', borderBottomWidth: 0.5, borderBottomColor: '#f3f4f6', backgroundColor: '#f9fafb' },
  th:         { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#fff', textTransform: 'uppercase' },
  td:         { fontSize: 8 },

  dutyBanner: { textAlign: 'center', fontSize: 10, fontFamily: 'Helvetica-Bold', marginVertical: 10, color: '#111' },

  section:    { marginBottom: 8 },
  sLbl:       { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#555', textTransform: 'uppercase', marginBottom: 2 },
  sVal:       { fontSize: 8, color: '#222' },

  totalsWrap: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 },
  metaBox:    { maxWidth: 220 },
  metaLine:   { fontSize: 8, color: '#444', marginBottom: 2 },
  totalsBox:  { minWidth: 200 },
  totRow:     { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
  totLbl:     { fontSize: 8, color: '#555' },
  totVal:     { fontSize: 8, color: '#111' },
  grossRow:   { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4, paddingTop: 4, borderTopWidth: 0.5, borderTopColor: '#d1d5db' },
  grossLbl:   { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#111' },
  grossVal:   { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#111' },

  bankBlock:  { marginTop: 14, textAlign: 'center' },
  bankLine:   { fontSize: 7, color: '#555', marginBottom: 1 },

  footer:     { marginTop: 8, borderTopWidth: 0.5, borderTopColor: '#d1d5db', paddingTop: 5 },
  footerLine: { fontSize: 6, color: '#888', lineHeight: 1.3, textAlign: 'center' },
})

function fmtDate(d: string | null | undefined) {
  if (!d) return '—'
  return new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
}
function fmtNum(n: number | null | undefined, dp = 2) {
  return n == null ? '—' : n.toFixed(dp)
}
function fmtMoney(n: number) {
  return n.toFixed(2)
}

export function InvoicePDF({ data }: { data: InvoicePDFData }) {
  const dutyVat = isDutyVat(data.invoice_category)
  const containerStr = data.containers
    .map(c => [c.container_no, c.seal_no && `SEAL: ${c.seal_no}`, c.container_type, c.said_to_contain].filter(Boolean).join(' - '))
    .join('; ')

  return (
    <Document>
      <Page size="A4" style={S.page}>
        {/* Letterhead */}
        <View style={S.header}>
          <View style={S.logoBox}>
            <Text style={S.logoText}>CY UK</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={[S.addrText, { fontFamily: 'Helvetica-Bold', fontSize: 9, color: '#222' }]}>{data.company.legal_name}</Text>
            {companyAddressLines(data.company).map((line, i) => <Text key={i} style={S.addrText}>{line}</Text>)}
            {data.company.phone && <Text style={S.addrText}>T: {data.company.phone}</Text>}
          </View>
        </View>

        <Text style={S.title}>
          {data.is_credit_note ? 'Credit Note' : (invoiceCategoryLabel(data.invoice_category) || 'Invoice')} - {data.invoice_no}
        </Text>
        {data.is_credit_note && data.credit_note_of_invoice_no && (
          <Text style={{ fontSize: 9, textAlign: 'center', marginBottom: 6, color: '#444' }}>Credit note against Invoice {data.credit_note_of_invoice_no}</Text>
        )}

        {/* Bill To + refs */}
        <View style={S.refRow2}>
          <View style={S.billBox}>
            <Text style={S.billLine}>{data.billing_name || '—'}</Text>
            {data.billing_address_lines.map((l, i) => <Text key={i} style={S.billLine}>{l}</Text>)}
          </View>
          <View style={S.refBox}>
            <View style={S.refRow}><Text style={S.refLbl}>Invoice Date: </Text><Text style={S.refVal}>{fmtDate(data.invoice_date)}</Text></View>
            <View style={S.refRow}><Text style={S.refLbl}>Job Number: </Text><Text style={S.refVal}>{data.job_ref || '—'}</Text></View>
            <View style={S.refRow}><Text style={S.refLbl}>Customer Account Number: </Text><Text style={S.refVal}>{data.customer_account_number || '—'}</Text></View>
            {data.your_reference && (
              <View style={S.refRow}><Text style={S.refLbl}>Your Reference: </Text><Text style={S.refVal}>{data.your_reference}</Text></View>
            )}
          </View>
        </View>

        {/* Shipper / Consignee */}
        <View style={S.addrRow}>
          <View style={S.addrBox}>
            <Text style={S.addrBoxHdr}>Shipper</Text>
            <Text style={S.addrLine}>{data.shipper_name || '—'}</Text>
            {data.shipper_address_lines.map((l, i) => <Text key={i} style={S.addrLine}>{l}</Text>)}
          </View>
          <View style={S.addrBox}>
            <Text style={S.addrBoxHdr}>Consignee</Text>
            <Text style={S.addrLine}>{data.consignee_name || '—'}</Text>
            {data.consignee_address_lines.map((l, i) => <Text key={i} style={S.addrLine}>{l}</Text>)}
          </View>
        </View>

        {/* Shipment details */}
        <View style={S.shipRow}>
          <View style={S.shipItem}><Text style={S.shipLbl}>Origin</Text><Text style={S.shipVal}>{data.origin || '—'}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Destination</Text><Text style={S.shipVal}>{data.destination || '—'}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Final Destination</Text><Text style={S.shipVal}>{data.final_destination || '—'}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Master</Text><Text style={S.shipVal}>{data.master_no || '—'}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Vessel</Text><Text style={S.shipVal}>{data.vessel || '—'}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Arrival</Text><Text style={S.shipVal}>{fmtDate(data.arrival_date)}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Pieces</Text><Text style={S.shipVal}>{data.pieces ?? '—'}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Cube</Text><Text style={S.shipVal}>{fmtNum(data.cube)}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Weight (Chgbl Kgs)</Text><Text style={S.shipVal}>{fmtNum(data.weight_chargeable, 0)}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Weight (Act Kgs)</Text><Text style={S.shipVal}>{fmtNum(data.weight_actual, 0)}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Number of Containers</Text><Text style={S.shipVal}>{data.containers.length || '—'}</Text></View>
          <View style={S.shipItem}><Text style={S.shipLbl}>Currency</Text><Text style={S.shipVal}>{data.currency}</Text></View>
          {containerStr !== '' && <Text style={[S.remarksVal, { width: '100%' }]}>Remarks: Container: {containerStr}</Text>}
        </View>

        {/* Charge lines */}
        <View style={S.table}>
          <View style={S.thead}>
            <Text style={[S.th, { flex: 1 }]}>Charge Description</Text>
            <Text style={[S.th, { flex: 1 }]}>Remarks</Text>
            <Text style={[S.th, { width: 60, textAlign: 'center' }]}>Taxable</Text>
            <Text style={[S.th, { width: 70, textAlign: 'right' }]}>Amount</Text>
          </View>
          {data.lines.map((l, i) => (
            <View key={i} style={i % 2 === 1 ? S.trowAlt : S.trow}>
              <Text style={[S.td, { flex: 1 }]}>{l.description}</Text>
              <Text style={[S.td, { flex: 1, color: '#666' }]}>{l.remarks || ''}</Text>
              <Text style={[S.td, { width: 60, textAlign: 'center' }]}>{l.taxable ? 'Yes' : 'No'}</Text>
              <Text style={[S.td, { width: 70, textAlign: 'right' }]}>{fmtMoney(l.amount)}</Text>
            </View>
          ))}
        </View>

        {dutyVat && <Text style={S.dutyBanner}>THIS IS A DUTY &amp; VAT INVOICE</Text>}

        {data.invoice_notes && (
          <View style={S.section}>
            <Text style={S.sLbl}>Notes</Text>
            {data.invoice_notes.split('\n').filter(Boolean).map((line, i) => (
              <Text key={i} style={S.sVal}>{line}</Text>
            ))}
          </View>
        )}

        {/* Totals + meta */}
        <View style={S.totalsWrap}>
          <View style={S.metaBox}>
            <Text style={S.metaLine}>VAT Number: {data.vat_number || '—'}</Text>
            {data.prepared_by && <Text style={S.metaLine}>Prepared By: {data.prepared_by}</Text>}
            <Text style={S.metaLine}>Please Pay By: {fmtDate(data.due_date)}</Text>
          </View>
          <View style={S.totalsBox}>
            <View style={S.totRow}><Text style={S.totLbl}>Taxable SubTotal:</Text><Text style={S.totVal}>{fmtMoney(data.taxable_subtotal)}</Text></View>
            <View style={S.totRow}><Text style={S.totLbl}>Non Taxable SubTotal:</Text><Text style={S.totVal}>{fmtMoney(data.non_taxable_subtotal)}</Text></View>
            <View style={S.totRow}><Text style={S.totLbl}>Net:</Text><Text style={S.totVal}>{fmtMoney(data.net)}</Text></View>
            <View style={S.totRow}><Text style={S.totLbl}>Tax Rate at {data.vat_rate_pct}%:</Text><Text style={S.totVal}>{fmtMoney(data.vat_amount)}</Text></View>
            <View style={S.grossRow}><Text style={S.grossLbl}>Gross:</Text><Text style={S.grossVal}>{fmtMoney(data.gross)}</Text></View>
          </View>
        </View>

        {/* Bank details */}
        <View style={S.bankBlock}>
          <Text style={S.bankLine}>Bank Details:</Text>
          <Text style={S.bankLine}>
            {data.company.bank_name}
            {data.company.bank_account_number && ` Account Number: ${data.company.bank_account_number}`}
            {data.company.bank_sort_code && ` Sort Code: ${data.company.bank_sort_code}`}
            {data.company.bank_iban && ` IBAN: ${data.company.bank_iban}`}
            {data.company.bank_swift && ` Swift: ${data.company.bank_swift}`}
          </Text>
        </View>

        <View style={S.footer}>
          <Text style={S.footerLine}>{companyRegistrationLine(data.company)}</Text>
          {data.company.bifa_line && <Text style={S.footerLine}>{data.company.bifa_line}</Text>}
        </View>
      </Page>
    </Document>
  )
}
